import React, { useState, useEffect, useCallback } from 'react';
import {
  Receipt, Plus, Filter, X, Eye, Pencil, Trash2, RefreshCw,
  ChevronLeft, ChevronRight, CreditCard
} from 'lucide-react';
import { LoadingSpinner } from '../../UI';
import {
  C, FONT_UI, FONT_DATA, Mono, Card, Pill, PageHeader, PrimaryButton, GhostButton,
  IconButton, Toolbar, StatCard, Table, TH, TD, EmptyState, Banner, fmtNum,
} from './theme';
import { invoiceAPI, clientAPI } from '../../../services';
import { useAuth } from '../../../context/AuthContext';
import LineItemsEditor from './billing/LineItemsEditor';
import { emptyLineItem, formatCurrency, formatDate, toDateInput } from './billing/lineItems';

const STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-800' },
  { value: 'sent', label: 'Sent', color: 'bg-blue-100 text-blue-800' },
  { value: 'partially_paid', label: 'Partially Paid', color: 'bg-amber-100 text-amber-800' },
  { value: 'paid', label: 'Paid', color: 'bg-green-100 text-green-800' },
  { value: 'overdue', label: 'Overdue', color: 'bg-red-100 text-red-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-gray-100 text-gray-500' }
];

const statusMeta = (status) => STATUSES.find((s) => s.value === status) || STATUSES[0];

// Maps the invoice lifecycle onto the shared Pill tones.
const STATUS_TONE = {
  draft: 'neutral',
  sent: 'info',
  partially_paid: 'warning',
  paid: 'success',
  overdue: 'danger',
  cancelled: 'neutral',
};

// Payment-derived statuses are owned by the server, so the form only offers the three
// an operator is allowed to set directly.
const SETTABLE_STATUSES = STATUSES.filter((s) => ['draft', 'sent', 'cancelled'].includes(s.value));

const PAYMENT_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'debit_card', label: 'Debit Card' },
  { value: 'other', label: 'Other' }
];

const emptyForm = () => ({
  title: '',
  client: '',
  items: [emptyLineItem()],
  discount: 0,
  issueDate: new Date().toISOString().split('T')[0],
  dueDate: '',
  status: 'draft',
  periodStart: '',
  periodEnd: '',
  notes: '',
  terms: ''
});

const BillingPage = () => {
  const { user } = useAuth();
  const canManage = (action) =>
    user?.role !== 'platform_staff' || user?.platformPermissions?.billing?.[action] === true;

  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const [filters, setFilters] = useState({ search: '', status: '', client: '', startDate: '', endDate: '' });
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [detail, setDetail] = useState(null);
  const [payingFor, setPayingFor] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', date: '', method: 'bank_transfer', reference: '', note: '' });

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: pagination.limit };
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      if (overdueOnly) params.overdueOnly = 'true';
      const response = await invoiceAPI.getInvoices(params);
      setInvoices(response.data.invoices || []);
      setPagination(response.data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
    } catch (err) {
      setError(err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [page, filters, overdueOnly, pagination.limit]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await invoiceAPI.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load billing stats:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    fetchStats();
    clientAPI
      .getClients()
      .then((res) => setClients(res.data.clients || []))
      .catch((err) => console.error('Failed to load clients:', err.message));
  }, [fetchStats]);

  const refreshAll = () => {
    fetchInvoices();
    fetchStats();
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setIsModalOpen(true);
  };

  const openEdit = (invoice) => {
    setEditingId(invoice._id);
    setForm({
      title: invoice.title || '',
      client: invoice.client?._id || invoice.client || '',
      items: invoice.items?.length ? invoice.items : [emptyLineItem()],
      discount: invoice.discount || 0,
      issueDate: toDateInput(invoice.issueDate),
      dueDate: toDateInput(invoice.dueDate),
      status: ['draft', 'sent', 'cancelled'].includes(invoice.status) ? invoice.status : 'sent',
      periodStart: toDateInput(invoice.billingPeriod?.start),
      periodEnd: toDateInput(invoice.billingPeriod?.end),
      notes: invoice.notes || '',
      terms: invoice.terms || ''
    });
    setIsModalOpen(true);
  };

  const openDetail = async (invoice) => {
    try {
      // The list response omits payment history, so pull the full record.
      const response = await invoiceAPI.getInvoice(invoice._id);
      setDetail(response.data);
    } catch (err) {
      setError(err.message || 'Failed to load invoice');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.client) {
      setError('Select a client');
      return;
    }
    if (!form.items.some((i) => i.description.trim())) {
      setError('Add at least one line item with a description');
      return;
    }

    const payload = {
      title: form.title,
      client: form.client,
      items: form.items.filter((i) => i.description.trim()),
      discount: Number(form.discount) || 0,
      issueDate: form.issueDate,
      dueDate: form.dueDate,
      status: form.status,
      billingPeriod: { start: form.periodStart || undefined, end: form.periodEnd || undefined },
      notes: form.notes,
      terms: form.terms
    };

    try {
      setSaving(true);
      if (editingId) {
        await invoiceAPI.updateInvoice(editingId, payload);
        setSuccess('Invoice updated');
      } else {
        await invoiceAPI.createInvoice(payload);
        setSuccess('Invoice created');
      }
      setIsModalOpen(false);
      refreshAll();
    } catch (err) {
      setError(err.message || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (invoice) => {
    if (!window.confirm(`Delete invoice ${invoice.invoiceNumber}? This cannot be undone.`)) return;
    try {
      await invoiceAPI.deleteInvoice(invoice._id);
      setSuccess('Invoice deleted');
      refreshAll();
    } catch (err) {
      setError(err.message || 'Failed to delete invoice');
    }
  };

  const openPayment = (invoice) => {
    setPayingFor(invoice);
    setPaymentForm({
      // Default to settling the invoice in full - the common case.
      amount: invoice.balance,
      date: new Date().toISOString().split('T')[0],
      method: 'bank_transfer',
      reference: '',
      note: ''
    });
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    setError('');
    try {
      setSaving(true);
      const response = await invoiceAPI.recordPayment(payingFor._id, {
        amount: Number(paymentForm.amount),
        date: paymentForm.date,
        method: paymentForm.method,
        reference: paymentForm.reference,
        note: paymentForm.note
      });
      setSuccess(`Payment recorded on ${payingFor.invoiceNumber}`);
      setPayingFor(null);
      // Keep an open detail modal in sync with the payment just recorded.
      if (detail && detail._id === response.data._id) {
        const fresh = await invoiceAPI.getInvoice(response.data._id);
        setDetail(fresh.data);
      }
      refreshAll();
    } catch (err) {
      setError(err.message || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePayment = async (invoiceId, paymentId) => {
    if (!window.confirm('Remove this payment? The invoice balance will be recalculated.')) return;
    try {
      await invoiceAPI.deletePayment(invoiceId, paymentId);
      const fresh = await invoiceAPI.getInvoice(invoiceId);
      setDetail(fresh.data);
      setSuccess('Payment removed');
      refreshAll();
    } catch (err) {
      setError(err.message || 'Failed to remove payment');
    }
  };

  return (
    <div style={{ fontFamily: FONT_UI }}>
      <PageHeader
        title="Billing & Invoices"
        subtitle="Generate invoices with full client details, GST and per-employee pricing, then record payments against them"
        actions={
          <>
            <GhostButton icon={RefreshCw} onClick={refreshAll}>
              Refresh
            </GhostButton>
            {canManage('create') && (
              <PrimaryButton icon={Plus} onClick={openCreate}>
                Create invoice
              </PrimaryButton>
            )}
          </>
        }
      />

      <Banner tone="error" message={error} onClose={() => setError('')} />
      <Banner tone="success" message={success} onClose={() => setSuccess('')} />

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
          <StatCard label="Total billed" value={formatCurrency(stats.invoiced)} hint={`${stats.invoiceCount || 0} invoices`} />
          <StatCard label="Collected" value={formatCurrency(stats.collected)} />
          <StatCard label="Outstanding" value={formatCurrency(stats.outstanding)} />
          <StatCard
            label="Paid"
            value={fmtNum(stats.byStatus?.find((b) => b._id === 'paid')?.count || 0)}
            hint="invoices settled"
          />
          {/* Clicking the overdue tile filters the table down to what is actually late. */}
          <button
            type="button"
            onClick={() => { setPage(1); setOverdueOnly((v) => !v); }}
            className="text-left rounded-xl p-4 transition-colors"
            style={{
              background: C.card,
              border: `1px solid ${overdueOnly ? C.rose : C.border}`,
              boxShadow: overdueOnly ? `0 0 0 3px ${C.rose}1A` : '0 1px 2px rgba(16,19,43,0.04)',
            }}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-[12px]" style={{ color: C.textMuted }}>Overdue</p>
              <Mono className="text-[11px] font-medium" style={{ color: C.rose }}>
                {stats.overdueCount || 0}
              </Mono>
            </div>
            <Mono className="block text-[26px] font-semibold mt-1.5" style={{ color: stats.overdueValue ? C.rose : C.ink }}>
              {formatCurrency(stats.overdueValue)}
            </Mono>
            <p className="text-[11px] mt-0.5" style={{ color: C.textFaint }}>
              {overdueOnly ? 'Filtering — click to clear' : 'Click to filter'}
            </p>
          </button>
        </div>
      )}

      <Toolbar
        value={filters.search}
        onChange={(v) => { setPage(1); setFilters({ ...filters, search: v }); }}
        placeholder="Search by invoice number or title..."
      >
        <GhostButton icon={Filter} onClick={() => setShowFilters(!showFilters)}>
          Filters
        </GhostButton>
      </Toolbar>

      {showFilters && (
        <Card className="p-4 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <select
              value={filters.status}
              onChange={(e) => { setPage(1); setFilters({ ...filters, status: e.target.value }); }}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink }}
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <select
              value={filters.client}
              onChange={(e) => { setPage(1); setFilters({ ...filters, client: e.target.value }); }}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink }}
            >
              <option value="">All clients</option>
              {clients.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => { setPage(1); setFilters({ ...filters, startDate: e.target.value }); }}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink, fontFamily: FONT_DATA }}
            />
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => { setPage(1); setFilters({ ...filters, endDate: e.target.value }); }}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink, fontFamily: FONT_DATA }}
            />
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : invoices.length === 0 ? (
          <EmptyState
            icon={Receipt}
            title="No invoices found"
            message={overdueOnly ? 'Nothing is overdue right now.' : 'Create an invoice to start billing a client.'}
          />
        ) : (
          <>
            <Table minWidth={980}>
              <thead>
                <tr>
                  <TH>Invoice</TH>
                  <TH>Client</TH>
                  <TH align="right">Amount (incl. GST)</TH>
                  <TH align="right">Balance due</TH>
                  <TH>Due date</TH>
                  <TH>Status</TH>
                  <TH align="right">Actions</TH>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv._id} className="hover:bg-slate-50">
                    <TD>
                      <Mono className="block font-medium">{inv.invoiceNumber}</Mono>
                      <span className="block text-[11px] max-w-[220px] truncate" style={{ color: C.textMuted }}>
                        {inv.title}
                      </span>
                    </TD>
                    <TD>{inv.client?.name || '—'}</TD>
                    <TD align="right">
                      <Mono className="font-medium">{formatCurrency(inv.total, inv.currency)}</Mono>
                      {inv.discountAmount > 0 && (
                        <span className="block text-[10px]" style={{ color: C.emerald }}>
                          discount applied
                        </span>
                      )}
                    </TD>
                    <TD align="right">
                      <Mono className="font-medium" style={{ color: inv.balance > 0 ? C.amber : C.emerald }}>
                        {inv.balance > 0 ? formatCurrency(inv.balance, inv.currency) : 'Settled'}
                      </Mono>
                    </TD>
                    <TD>
                      <Mono style={{ color: C.textMuted }}>{formatDate(inv.dueDate)}</Mono>
                    </TD>
                    <TD>
                      <Pill tone={STATUS_TONE[inv.status] || 'neutral'}>{statusMeta(inv.status).label}</Pill>
                    </TD>
                    <TD align="right">
                      <div className="flex items-center justify-end gap-1">
                        <IconButton icon={Eye} title="View invoice" onClick={() => openDetail(inv)} />
                        {canManage('update') && inv.balance > 0 && inv.status !== 'cancelled' && (
                          <IconButton
                            icon={CreditCard}
                            title="Record payment"
                            tone={C.emerald}
                            onClick={() => openPayment(inv)}
                          />
                        )}
                        {canManage('update') && (
                          <IconButton icon={Pencil} title="Edit invoice" onClick={() => openEdit(inv)} />
                        )}
                        {canManage('delete') && inv.amountPaid === 0 && (
                          <IconButton icon={Trash2} title="Delete invoice" tone={C.rose} onClick={() => handleDelete(inv)} />
                        )}
                      </div>
                    </TD>
                  </tr>
                ))}
              </tbody>
            </Table>

            {pagination.pages > 1 && (
              <div
                className="flex items-center justify-between px-5 py-3"
                style={{ borderTop: `1px solid ${C.border}` }}
              >
                <Mono className="text-[12px]" style={{ color: C.textMuted }}>
                  Page {pagination.page} of {pagination.pages} · {pagination.total} invoices
                </Mono>
                <div className="flex items-center gap-2">
                  <IconButton
                    icon={ChevronLeft}
                    title="Previous page"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pagination.page <= 1}
                  />
                  <IconButton
                    icon={ChevronRight}
                    title="Next page"
                    onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                    disabled={pagination.page >= pagination.pages}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Create / edit modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-gray-900">{editingId ? 'Edit Invoice' : 'New Invoice'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    required
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g. DigiHR Subscription - Q3 2026"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
                  <select
                    required
                    value={form.client}
                    onChange={(e) => setForm({ ...form, client: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a client</option>
                    {clients.map((c) => (
                      <option key={c._id} value={c._id}>{c.name} ({c.code})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Line Items *</label>
                <LineItemsEditor
                  items={form.items}
                  onChange={(items) => setForm({ ...form, items })}
                  discount={form.discount}
                  onDiscountChange={(discount) => setForm({ ...form, discount })}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
                  <input
                    type="date"
                    value={form.issueDate}
                    onChange={(e) => setForm({ ...form, issueDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
                  <input
                    type="date"
                    required
                    value={form.dueDate}
                    onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {SETTABLE_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">Paid / overdue are set automatically from payments.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing Period Start</label>
                  <input
                    type="date"
                    value={form.periodStart}
                    onChange={(e) => setForm({ ...form, periodStart: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing Period End</label>
                  <input
                    type="date"
                    value={form.periodEnd}
                    onChange={(e) => setForm({ ...form, periodEnd: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Terms &amp; Conditions</label>
                  <textarea
                    rows={3}
                    value={form.terms}
                    onChange={(e) => setForm({ ...form, terms: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invoice detail with payment history */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{detail.invoiceNumber}</h2>
                <p className="text-sm text-gray-500">{detail.title}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Client</p>
                  <p className="font-medium text-gray-900">{detail.client?.name || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Issued</p>
                  <p className="font-medium text-gray-900">{formatDate(detail.issueDate)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Due</p>
                  <p className="font-medium text-gray-900">{formatDate(detail.dueDate)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusMeta(detail.status).color}`}>
                    {statusMeta(detail.status).label}
                  </span>
                </div>
              </div>

              {detail.quotation && (
                <p className="text-sm text-gray-500">Converted from quotation {detail.quotation.quoteNumber}</p>
              )}

              <LineItemsEditor items={detail.items || []} onChange={() => {}} discount={detail.discount} currency={detail.currency} disabled />

              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg text-sm">
                <div>
                  <p className="text-gray-500">Total</p>
                  <p className="font-bold text-gray-900">{formatCurrency(detail.total, detail.currency)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Paid</p>
                  <p className="font-bold text-green-600">{formatCurrency(detail.amountPaid, detail.currency)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Balance</p>
                  <p className={`font-bold ${detail.balance > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                    {formatCurrency(detail.balance, detail.currency)}
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-700">Payment History</p>
                  {canManage('update') && detail.balance > 0 && detail.status !== 'cancelled' && (
                    <button
                      onClick={() => openPayment(detail)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
                    >
                      <Plus className="w-4 h-4" /> Record payment
                    </button>
                  )}
                </div>
                {(detail.payments || []).length === 0 ? (
                  <p className="text-sm text-gray-500 py-3">No payments recorded yet.</p>
                ) : (
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                    {detail.payments.map((p) => (
                      <div key={p._id} className="flex items-center justify-between px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{formatCurrency(p.amount, detail.currency)}</p>
                          <p className="text-xs text-gray-500">
                            {formatDate(p.date)} &middot; {PAYMENT_METHODS.find((m) => m.value === p.method)?.label || p.method}
                            {p.reference ? ` · ${p.reference}` : ''}
                            {p.recordedBy?.name ? ` · by ${p.recordedBy.name}` : ''}
                          </p>
                        </div>
                        {canManage('delete') && (
                          <button
                            onClick={() => handleDeletePayment(detail._id, p._id)}
                            className="text-gray-400 hover:text-red-600"
                            title="Remove payment"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {detail.notes && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Notes</p>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{detail.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Record payment */}
      {payingFor && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Record Payment</h2>
              <button onClick={() => setPayingFor(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleRecordPayment} className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                {payingFor.invoiceNumber} &middot; outstanding{' '}
                <span className="font-semibold text-gray-900">{formatCurrency(payingFor.balance, payingFor.currency)}</span>
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                <input
                  type="number"
                  required
                  min="0.01"
                  max={payingFor.balance}
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={paymentForm.date}
                    onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
                  <select
                    value={paymentForm.method}
                    onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                <input
                  type="text"
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                  placeholder="UTR / cheque number / txn id"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea
                  rows={2}
                  value={paymentForm.note}
                  onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setPayingFor(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? 'Recording...' : 'Record Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingPage;
